import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectMeForUpdate } from '@/store/me';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import {
  createTimezoneSearchFilter,
  formatLiveGmtOffset,
} from '@/utils/timezonePlaceSearch';

export interface TimeZoneOption {
  label: string;
  value: string;
  offset: string;
  abbreviation: string;
  fullName: string;
  shortName: string;
}

type TimeZoneMap = Record<string, unknown>;

type TimeZoneConstant = {
  fullName?: string;
  shortName?: string;
  abbreviation?: string;
};

const tzOptions = (timeZones: TimeZoneMap | undefined): TimeZoneOption[] => {
  const timezoneData =
    Object.keys(timeZones || {}).length > 0
      ? (timeZones as TimeZoneMap)
      : (TIME_ZONE_CONSTANTS as TimeZoneMap);

  return Object.keys(timezoneData || {}).map((key) => {
    const { fullName, shortName, abbreviation } = ((
      TIME_ZONE_CONSTANTS as Record<string, TimeZoneConstant>
    )[key] || {}) as TimeZoneConstant;
    const liveOffset = formatLiveGmtOffset(key);

    return {
      label: `${fullName} (${liveOffset}) - ${shortName}`,
      value: key,
      offset: liveOffset,
      abbreviation: abbreviation || '',
      fullName: fullName || '',
      shortName: shortName || '',
    };
  });
};

export const useTzOptions = () => {
  const { time_zones } = (useSelector(selectMeForUpdate) || {}) as {
    time_zones?: TimeZoneMap;
  };

  const timeZoneOptions = useMemo(() => tzOptions(time_zones), [time_zones]);

  // Shared with every other picker so city/country search behaves the same.
  const customFilterOption = useMemo(
    () => createTimezoneSearchFilter(timeZoneOptions),
    [timeZoneOptions]
  );

  return { timeZoneOptions, customFilterOption };
};
